'use client';

import { clsx } from 'clsx';
import { FC, ReactNode, useRef } from 'react';

import { CarouselControls } from './CarouselControls';
import { CarouselTrack } from './CarouselTrack';

type Props = {
  className?: string;
  tiles: { id: string; tile: ReactNode }[];
  controls?: {
    prev: (onClick: () => void) => ReactNode;
    next: (onClick: () => void) => ReactNode;
  };
  carouselName?: string;
  variant?: 'default' | 'recently-viewed' | 'car-match';
};

export const Carousel: FC<Props> = ({ className, tiles, controls, carouselName, variant = 'default' }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  const handleNextClick = () => {
    if (rootRef.current) {
      const scrollWidth = getScrollByLength(rootRef.current);
      const carouselTrack = rootRef.current.querySelector('.carouselTrack');

      if (scrollWidth !== null && carouselTrack !== null) {
        carouselTrack.scrollTo({
          left: Math.min(carouselTrack.scrollWidth, carouselTrack.scrollLeft + scrollWidth),
          behavior: 'smooth',
        });
      }
    }
  };

  const handlePrevClick = () => {
    if (rootRef.current) {
      const scrollWidth = getScrollByLength(rootRef.current);
      const carouselTrack = rootRef.current.querySelector('.carouselTrack');

      if (scrollWidth !== null && carouselTrack !== null) {
        carouselTrack.scrollTo({
          left: Math.max(0, carouselTrack.scrollLeft - scrollWidth),
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <div
      className={clsx('group/carousel relative', className, {
        'overflow-hidden': variant !== 'car-match',
      })}
      ref={rootRef}
      data-testid={carouselName}
    >
      <CarouselTrack variant={variant} carouselName={carouselName}>
        {tiles.map(({ id, tile }, index) => (
          <div
            className={clsx('carouselTile min-h-full flex-none basis-72 snap-center sm:basis-80 md:basis-88')}
            key={id}
            data-index={index}
            data-testid="carouselTile"
          >
            {tile}
          </div>
        ))}
      </CarouselTrack>
      <CarouselControls
        handleNextClick={handleNextClick}
        handlePrevClick={handlePrevClick}
        controls={controls}
        carouselName={carouselName}
        variant={variant}
      />
    </div>
  );
};

const getScrollByLength = (root: HTMLDivElement): number | null => {
  const rootWidth = root.clientWidth;
  // Get the list of tiles in the carousel
  const tiles = root.querySelectorAll('.carouselTile');
  if (tiles.length) {
    const tileWidth = tiles[0].clientWidth;
    // Scroll by at least a tile width or 60% of the track
    const scrollWidth = Math.max(tileWidth, Math.floor(rootWidth / tileWidth) * 0.6 * tileWidth);

    return scrollWidth;
  }

  return null;
};
